import { Alert, CircularProgress } from '@mui/material';
import { FileText, Image as ImageIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useUserMediaUsage } from '../hooks/useUsers';
import type { MediaKind, UserMediaKindUsage } from '../types';
import { formatAdminNumber } from '../../../../utils/numberFormat';
import { formatDateTime } from '../../../../utils/dateFormat';

interface UserMediaUsageSectionProps {
  publicId?: string;
}

const text = {
  allTime: 'All time',
  auditOnlyFailures: 'Failures are not counted toward usage.',
  cycle: 'cycle',
  dailyAriaSuffix: 'used today',
  today: 'Today (UTC)',
  thisCycle: 'This cycle',
  disabledBlocked: 'Disabled (blocked)',
  failedThisCycle: 'Failed this cycle:',
  failedTotal: 'Failed total:',
  kinds: {
    file: 'Files',
    image: 'Images',
  },
  lastGenerated: 'Last generated',
  noActivePackage: 'No active package',
  noPackageLimit: 'No package limit',
  normal: 'Normal',
  package: 'Package',
  resets: 'resets',
  sectionAria: 'Files and images usage',
  title: 'Files & images',
  unableToLoad: 'Unable to load files & images usage.',
  unlimited: 'Unlimited',
  usageAriaSuffix: 'used this cycle',
  viaApiKey: 'Via API key (cycle / all time)',
} as const;

const MEDIA_KIND_META: Record<MediaKind, { label: string; Icon: LucideIcon; colorClass: string }> =
  {
    file: { label: text.kinds.file, Icon: FileText, colorClass: 'text-sky-500' },
    image: { label: text.kinds.image, Icon: ImageIcon, colorClass: 'text-pink-500' },
  };

const limitLabel = (enabled: boolean, limit: number | null) => {
  if (!enabled) return '0';
  if (limit === null) return text.unlimited;
  return formatAdminNumber(limit);
};

const usagePercent = (enabled: boolean, used: number, limit: number | null) => {
  if (!enabled) return 100;
  if (limit === null) return 0;
  if (limit === 0) return 100;
  return Math.min(100, Math.round((used / limit) * 100));
};

const stateBadge = (usage: UserMediaKindUsage) => {
  if (!usage.enabled) {
    return {
      label: text.disabledBlocked,
      className: 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-950/30',
    };
  }

  if (!usage.governed) {
    return {
      label: text.noPackageLimit,
      className:
        'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-950/30',
    };
  }

  return {
    label: text.normal,
    className:
      'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-950/30',
  };
};

interface UsageMeterProps {
  title: string;
  ariaLabel: string;
  enabled: boolean;
  used: number;
  limit: number | null;
}

const UsageMeter: React.FC<UsageMeterProps> = ({ title, ariaLabel, enabled, used, limit }) => {
  const percent = usagePercent(enabled, used, limit);
  const isFull = percent >= 100 && limit !== null;

  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
        {title}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-gray-900 dark:text-white">
          {formatAdminNumber(used)}
        </span>
        <span className="text-sm text-gray-500 dark:text-slate-400">
          / {limitLabel(enabled, limit)}
        </span>
      </div>
      {enabled && limit !== null && (
        <div
          className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={ariaLabel}
        >
          <div
            className={`h-full rounded-full ${isFull ? 'bg-red-500' : 'bg-blue-500'}`}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
    </div>
  );
};

const MediaKindCard: React.FC<{ usage: UserMediaKindUsage }> = ({ usage }) => {
  const { label, Icon, colorClass } = MEDIA_KIND_META[usage.kind];
  const badge = stateBadge(usage);

  return (
    <div className="rounded-lg border border-gray-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-slate-400">
          <Icon size={17} className={colorClass} />
          {label}
        </div>
        <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${badge.className}`}>
          {badge.label}
        </span>
      </div>
      <div className="mt-3 space-y-3">
        <UsageMeter
          title={text.today}
          ariaLabel={`${label} ${text.dailyAriaSuffix}`}
          enabled={usage.enabled}
          used={usage.usedToday}
          limit={usage.dailyLimit}
        />
        <UsageMeter
          title={text.thisCycle}
          ariaLabel={`${label} ${text.usageAriaSuffix}`}
          enabled={usage.enabled}
          used={usage.usedThisCycle}
          limit={usage.limit}
        />
      </div>
      <dl className="mt-3 space-y-1 text-xs text-gray-500 dark:text-slate-400">
        <div className="flex justify-between gap-2">
          <dt>{text.allTime}</dt>
          <dd className="font-medium text-gray-900 dark:text-white">
            {formatAdminNumber(usage.usedTotal)}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>{text.viaApiKey}</dt>
          <dd className="font-medium text-gray-900 dark:text-white">
            {formatAdminNumber(usage.usedViaApiKeyThisCycle)} /{' '}
            {formatAdminNumber(usage.usedViaApiKeyTotal)}
          </dd>
        </div>
        <div className="pt-2 text-[11px] uppercase tracking-wide text-gray-400 dark:text-slate-500">
          {text.auditOnlyFailures}
        </div>
        <div className="flex justify-between gap-2">
          <dt>{text.failedThisCycle}</dt>
          <dd className="font-medium text-gray-700 dark:text-slate-200">
            {formatAdminNumber(usage.failedThisCycle)}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>{text.failedTotal}</dt>
          <dd className="font-medium text-gray-700 dark:text-slate-200">
            {formatAdminNumber(usage.failedTotal)}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>{text.lastGenerated}</dt>
          <dd className="font-medium text-gray-900 dark:text-white">
            {formatDateTime(usage.lastGeneratedAt) || '—'}
          </dd>
        </div>
      </dl>
    </div>
  );
};

export const UserMediaUsageSection: React.FC<UserMediaUsageSectionProps> = ({ publicId }) => {
  const { data, isLoading, isError, error } = useUserMediaUsage(publicId);

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <CircularProgress size={22} sx={{ color: '#3B82F6' }} />
      </div>
    );
  }

  if (isError) {
    return <Alert severity="error">{error?.message || text.unableToLoad}</Alert>;
  }

  if (!data) return null;

  return (
    <section aria-label={text.sectionAria} className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{text.title}</h3>
        <p className="text-xs text-gray-500 dark:text-slate-400">
          {data.package ? `${text.package} ${data.package.name} · ` : `${text.noActivePackage} · `}
          {text.cycle} {formatDateTime(data.cycleStart)} → {text.resets}{' '}
          {formatDateTime(data.resetsAt)} · {text.today} {text.resets}{' '}
          {formatDateTime(data.dayResetsAt)}
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {data.kinds.map((usage) => (
          <MediaKindCard key={usage.kind} usage={usage} />
        ))}
      </div>
    </section>
  );
};
